import type { CategoryContent } from './row.js';

export const PHARMACEUTICALS: CategoryContent = {
  bscba: [
    [
      'What does ADME stand for in pharmacokinetics?',
      [
        'Absorption, Distribution, Metabolism, Excretion',
        'Acetylation, Deamination, Methylation, Esterification',
        'Activation, Degradation, Modification, Elimination',
        'Adherence, Dosing, Monitoring, Efficacy',
      ],
      0,
      'ADME encompasses the four fundamental processes describing how the body handles a drug.',
    ],
    [
      'Which type of drug receptor typically operates through G-protein coupling?',
      [
        'GPCRs',
        'Ion channel receptors',
        'Nuclear receptors',
        'Enzyme-linked receptors',
      ],
      0,
      'GPCRs activate intracellular signaling via heterotrimeric G-proteins and are the largest cell surface receptor family.',
    ],
    [
      'What is the primary goal of Phase II clinical trials?',
      [
        'Assess efficacy and optimal dosing',
        'Evaluate long-term safety in large populations',
        'Establish basic safety and tolerability in healthy volunteers',
        'Monitor rare adverse events in real-world use',
      ],
      0,
      'Phase II trials focus on proof-of-concept and dose-response in small patient groups with efficacy endpoints.',
    ],
    [
      'Which antibiotic was discovered by Alexander Fleming through accidental contamination?',
      [
        'Penicillin',
        'Streptomycin',
        'Chloramphenicol',
        'Tetracycline',
      ],
      0,
      'Fleming\'s 1928 observation of mold inhibiting bacterial growth led to penicillin, the first widely-used antibiotic.',
    ],
    [
      'What describes the process by which a drug first enters the bloodstream after administration?',
      [
        'Absorption',
        'Distribution',
        'Metabolism',
        'Excretion',
      ],
      0,
      'Absorption is the transition of a drug from its administration site into systemic circulation.',
    ],
    [
      'An agonist differs from an antagonist because it:',
      [
        'Binds and activates the receptor',
        'Blocks a receptor without triggering a response',
        'Increases enzyme activity irreversibly',
        'Prevents drug metabolism in the liver',
      ],
      0,
      'An agonist binds and activates a receptor; an antagonist binds competitively without activation.',
    ],
    [
      'What is drug half-life?',
      [
        'Time for plasma concentration to drop 50%',
        'The time it takes for a drug to reach maximum concentration',
        'The total time a drug remains in the body',
        'The duration of therapeutic effect',
      ],
      0,
      'Half-life is the time for plasma concentration to drop 50%; it predicts accumulation with repeated dosing.',
    ],
    [
      'Which organ is primarily responsible for drug metabolism?',
      [
        'Liver',
        'Kidney',
        'Stomach',
        'Lungs',
      ],
      0,
      'The liver hosts cytochrome P450 enzymes that transform most drugs into metabolites for elimination.',
    ],
    [
      'What is bioavailability?',
      [
        'Fraction of dose reaching circulation',
        'The concentration of drug at the site of action',
        'The rate at which a drug is absorbed',
        'The ability of a drug to cross the blood-brain barrier',
      ],
      0,
      'Bioavailability (F) is the fraction entering systemic circulation; IV administration equals 100%.',
    ],
    [
      'In FDA approval, what does "Orphan Drug" designation indicate?',
      [
        'A rare-disease drug (<200,000 US patients)',
        'A medication without a patent or brand name protection',
        'An unapproved experimental compound in early trials',
        'A drug with no known manufacturer or supply chain',
      ],
      0,
      'Orphan Drug status encourages rare disease treatments with extended patent protection and incentives.',
    ],
    [
      'What is a drug-drug interaction?',
      [
        'Effect on efficacy or safety from a co-administered drug',
        'The competition between two drugs for the same receptor site',
        'The chemical reaction between two drug molecules in solution',
        'The sequential administration of two medications on the same day',
      ],
      0,
      'Drug interactions occur when one drug alters the absorption, metabolism, or effect of another.',
    ],
    [
      'Which route of administration typically has the slowest absorption?',
      [
        'Transdermal',
        'Intravenous',
        'Oral',
        'Intramuscular',
      ],
      0,
      'Transdermal patches deliver drugs slowly; IV is instantaneous, oral and IM are intermediate.',
    ],
    [
      'What does the term "generic" mean for a pharmaceutical drug?',
      [
        'A drug no longer under patent protection, made by any manufacturer',
        'An unbranded drug that is less effective than the original formulation',
        'A drug that works for multiple diseases',
        'A drug in its earliest development stage',
      ],
      0,
      'A generic is a non-proprietary drug with the same active ingredient; bioequivalence is verified.',
    ],
    [
      'What causes a type A adverse drug reaction?',
      [
        'An exaggerated, predictable drug response',
        'An idiosyncratic reaction unrelated to pharmacology',
        'An allergic or immune-mediated response',
        'A reaction that only occurs after long-term exposure',
      ],
      0,
      'Type A (augmented) reactions are dose-related and pharmacologically predictable.',
    ],
    [
      'What is the primary component of a pharmaceutical formulation that dissolves or disperses the active ingredient?',
      [
        'Excipient',
        'Binder',
        'Preservative',
        'Chelating agent',
      ],
      0,
      'Excipients are inert substances enabling drug delivery, forming the formulation\'s bulk.',
    ],
  ],
  msc: [
    [
      'What is allosteric modulation?',
      [
        'Regulation via binding at a site distant from the orthosteric site',
        'Irreversible inhibition of an enzyme at its active site',
        'Competitive inhibition where the inhibitor and substrate occupy the same binding site',
        'The saturation of enzyme kinetics at high substrate concentrations',
      ],
      0,
      'Allosteric binding changes receptor conformation, altering orthosteric site affinity without direct competition.',
    ],
    [
      'Which cytochrome P450 enzyme is responsible for metabolizing approximately 25% of all drugs?',
      [
        'CYP3A4',
        'CYP2D6',
        'CYP2C9',
        'CYP1A2',
      ],
      0,
      'CYP3A4 is the most abundant hepatic enzyme metabolizing the largest drug diversity.',
    ],
    [
      'In Phase I/II trials, what is a primary difference in their statistical design?',
      [
        'Phase I is open-label safety-focused; Phase II is randomized',
        'Phase I enrolls patients; Phase II enrolls healthy volunteers',
        'Phase I uses crossover designs; Phase II uses parallel groups',
        'Phase I assesses metabolism; Phase II assesses clearance',
      ],
      0,
      'Phase I prioritizes safety in open cohorts; Phase II adds efficacy endpoints with randomization.',
    ],
    [
      'What does pharmacodynamic modeling using Emax equations describe?',
      [
        'The sigmoidal concentration-effect relationship',
        'The linear elimination of a drug at steady state',
        'The rate of drug absorption across the gastrointestinal tract',
        'The volume of distribution in different tissue compartments',
      ],
      0,
      'Emax models relate effect to concentration via maximal response and potency (EC50), capturing saturation.',
    ],
    [
      'How does drug chirality influence pharmacokinetics?',
      [
        'Enantiomers may metabolize at different rates',
        'Chiral drugs are always absorbed faster than achiral ones',
        'Chirality has no impact on pharmacokinetics, only on potency',
        'Enantiomers are rapidly interconverted in the bloodstream',
      ],
      0,
      'Enantiomers can have markedly different metabolism and transporter affinity.',
    ],
    [
      'What is first-pass metabolism?',
      [
        'Pre-systemic hepatic or intestinal metabolism',
        'The initial elimination of a drug during the absorption phase',
        'The metabolism of a drug by the kidney',
        'The inactivation of a prodrug into its active form',
      ],
      0,
      'First-pass metabolism (pre-systemic) occurs as absorbed drug transits portal circulation.',
    ],
    [
      'How does plasma protein binding affect pharmacokinetics?',
      [
        'Lower free concentration and altered clearance',
        'Protein binding increases the volume of distribution uniformly',
        'Bound drug contributes equally to pharmacological effect as unbound drug',
        'Protein binding has no kinetic consequence',
      ],
      0,
      'Only unbound drug can be cleared or exert effect; binding sequesters drug, reducing clearance.',
    ],
    [
      'What is the mechanism of action of beta-blockers?',
      [
        'Adrenergic antagonism',
        'Agonism at muscarinic acetylcholine receptors',
        'Inhibition of calcium channels',
        'Direct vasodilation via nitric oxide',
      ],
      0,
      'Beta-blockers competitively inhibit norepinephrine/epinephrine at β-adrenergic receptors.',
    ],
    [
      'What is a prodrug?',
      [
        'An inactive compound metabolized to an active form',
        'A drug intermediate during manufacturing',
        'A partially effective drug requiring combination therapy',
        'A drug in its earliest preclinical stage',
      ],
      0,
      'Prodrugs are inactive compounds; metabolism converts them to active metabolites.',
    ],
    [
      'What determines steady-state drug concentration at a given dose interval?',
      [
        'Clearance relative to dose interval',
        'The volume of distribution alone',
        'The absorption rate constant',
        'The binding affinity to plasma proteins',
      ],
      0,
      'At steady state, input equals elimination. Accumulation depends on half-life relative to dosing interval.',
    ],
    [
      'What is pharmacogenomics?',
      [
        'Genetic variation in drug-metabolizing enzymes',
        'The development of new chemical entities from genetic material',
        'The use of drugs to modify gene expression',
        'The inheritance of drug metabolism patterns across generations',
      ],
      0,
      'Pharmacogenomics examines how genetic polymorphisms affect drug metabolism, efficacy, and adverse reactions.',
    ],
    [
      'What role do drug transporters play in pharmacokinetics?',
      [
        'Active transport of drugs across membranes',
        'Passive diffusion of all drugs across the blood-brain barrier',
        'Binding of drugs to carrier proteins in plasma',
        'Inactivation of drugs through enzymatic degradation',
      ],
      0,
      'Transporters actively pump drugs into/out of cells, influencing GI absorption and hepatic clearance.',
    ],
    [
      'Why might a Phase III clinical trial fail even if Phase II was successful?',
      [
        'Larger populations reveal poor-response subgroups',
        'Phase III requires different endpoints than Phase II',
        'Drug metabolism changes with larger sample sizes',
        'Regulatory requirements are stricter for Phase III',
      ],
      0,
      'Phase III enrolls far more subjects; rare or subgroup-specific adverse effects often emerge.',
    ],
    [
      'What is a plausible explanation for loss of efficacy after prolonged drug exposure?',
      [
        'Receptor desensitization or downregulation',
        'Increased drug metabolism over time',
        'Renal insufficiency developing during therapy',
        'Degradation of the drug molecule in the bloodstream',
      ],
      0,
      'Chronic agonist exposure triggers receptor desensitization or downregulation, reducing responsiveness.',
    ],
    [
      'What is the significance of CYP450 induction in drug interactions?',
      [
        'It increases metabolic enzyme expression',
        'Induction prevents a drug from being metabolized',
        'Induction occurs only with chronic dosing of a single drug',
        'Inducers block transporter-mediated clearance',
      ],
      0,
      'Inducers upregulate CYP450 expression, increasing metabolism of co-administered substrates.',
    ],
  ],
  phd: [
    [
      'What is positive allosteric modulation (PAM) and how does it differ mechanistically from orthosteric agonism?',
      [
        'PAM boosts agonist affinity without direct activation',
        'PAMs have slower kinetics than agonists',
        'PAMs only affect G-protein coupled receptors',
        'PAMs increase receptor expression while agonists increase signaling',
      ],
      0,
      'PAMs shift agonist dose-response rightward or raise plateau; agonists activate orthosterically.',
    ],
    [
      'How do CYP450 inhibition kinetics (competitive vs. mechanism-based) determine DDI severity?',
      [
        'Mechanism-based inhibitors irreversibly inactivate the enzyme',
        'Non-competitive inhibition is always reversible',
        'Competitive inhibition always produces clinical DDIs; non-competitive does not',
        'Both are equally time-dependent',
      ],
      0,
      'Competitive inhibition is reversible; mechanism-based inhibitors permanently inactivate enzyme progressively.',
    ],
    [
      'What is the primary goal of physiologically-based pharmacokinetic (PBPK) modeling?',
      [
        'Predict tissue drug concentrations physiologically',
        'Replace clinical trials with in silico simulations',
        'Determine whether a drug crosses the blood-brain barrier',
        'Calculate the volume of distribution for all drugs',
      ],
      0,
      'PBPK integrates tissue perfusion and enzyme kinetics to predict whole-body and compartment-specific PK.',
    ],
    [
      'What distinguishes biased signaling at GPCRs and why is it pharmacologically important?',
      [
        'Ligands preferentially activate one pathway over another',
        'Biased signaling means the receptor is inhibited',
        'All GPCR agonists activate identical intracellular pathways',
        'Biased signaling only occurs with antagonists',
      ],
      0,
      'Biased agonism exploits pathway-selective efficacy; may separate therapeutic effects from toxicity.',
    ],
    [
      'How is the PK/PD index (e.g., AUC/MIC, Cmax/MIC) optimized to predict clinical efficacy?',
      [
        'Data are fit to sigmoidal PK/PD models',
        'PK/PD indices are fixed numbers independent of the drug',
        'Only AUC is relevant; peak concentration (Cmax) is irrelevant',
        'PK/PD indices are calculated post-hoc after approval',
      ],
      0,
      'PK/PD index optimization uses dose studies to define exposure-response relationship.',
    ],
    [
      'By what mechanism can substrate inhibition of a CYP450 enzyme paradoxically increase its own metabolic activity?',
      [
        'It allosterically activates the enzyme itself',
        'Substrate inhibition always decreases enzyme activity',
        'CYP450 enzymes cannot be allosterically modulated',
        'Substrate inhibition only affects kidney function',
      ],
      0,
      'Some CYP450s (notably CYP3A4) exhibit allosteric substrate binding that activates catalysis.',
    ],
    [
      'What is the mechanism of action of direct-acting antivirals (DAAs) in hepatitis C, and why did they revolutionize treatment?',
      [
        'DAAs directly inhibit a viral NS enzyme',
        'DAAs inhibit host immune responses',
        'DAAs work by stimulating interferon-alpha production',
        'DAAs target hepatitis C antibodies',
      ],
      0,
      'HCV DAAs block viral replication enzymes with pangenotypic activity, transforming HCV to curable.',
    ],
    [
      'How do P-glycoprotein (MDR1/ABCB1) transporters affect oral bioavailability and CNS penetration of drugs?',
      [
        'P-gp effluxes substrates from enterocytes and the BBB',
        'P-gp increases the absorption of all drugs in the intestine',
        'P-gp has no effect on the blood-brain barrier',
        'P-gp only transports large molecules',
      ],
      0,
      'P-gp actively extrudes substrates from GI enterocytes and BBB endothelium; inhibitors increase exposure.',
    ],
    [
      'What is stereoselective metabolism and how does it complicate enantiomeric drug design?',
      [
        'Enzymes preferentially metabolize one enantiomer',
        'Stereoselective metabolism only occurs in the liver',
        'Both enantiomers are always metabolized at identical rates',
        'Stereoselective metabolism only affects peptide drugs',
      ],
      0,
      'CYP450s distinguish stereochemistry; single-enantiomer drugs may have longer half-lives.',
    ],
    [
      'What are the key components of target validation in drug discovery?',
      [
        'Evidence linking modulation to disease phenotype',
        'Testing whether a compound binds to the proposed target',
        'Confirmation of expression levels at the disease site',
        'Assessment of off-target binding profiles',
      ],
      0,
      'Target validation requires causal links: genetic perturbation, pharmacological inhibition, and phenotype reversal.',
    ],
    [
      'How are sample sizes calculated for Phase II dose-escalation trials, and what statistical considerations apply?',
      [
        'Powered for a minimally important difference',
        'Phase II trials require no statistical planning',
        'Sample sizes in Phase II are always larger than Phase III',
        'Phase II trials use no control groups',
      ],
      0,
      'Phase II sample sizes are powered for minimally important PK/PD or efficacy differences with multiplicity.',
    ],
    [
      'What is a CYP2D6 poor metabolizer genotype and how does it affect codeine efficacy?',
      [
        'It blocks codeine-to-morphine conversion',
        'Poor metabolizers have enhanced codeine efficacy',
        'CYP2D6 status only affects acetaminophen metabolism',
        'Poor metabolizers are rare and clinically irrelevant',
      ],
      0,
      'CYP2D6 PMs cannot activate codeine prodrug to morphine; ultra-rapid metabolizers risk toxicity.',
    ],
    [
      'What role do OATP transporters play in determining hepatic clearance and DDI potential for atorvastatin?',
      [
        'OATP1B1/1B3 uptake is rate-limiting',
        'OATP transporters export drugs from hepatocytes',
        'Atorvastatin is exclusively metabolized by CYP3A4',
        'OATP inhibition always reduces drug efficacy',
      ],
      0,
      'OATP1B1/1B3 mediate sinusoidal uptake; inhibitors reduce atorvastatin clearance 3-4-fold.',
    ],
    [
      'What is a "mechanistic biomarker" in clinical trials, and how is it distinct from a "clinical endpoint"?',
      [
        'They reflect target engagement, not outcomes',
        'Mechanistic biomarkers and clinical endpoints are the same',
        'Clinical endpoints are always faster to assess than biomarkers',
        'Mechanistic biomarkers predict clinical endpoints with 100% accuracy',
      ],
      0,
      'PD biomarkers reflect target engagement; clinical endpoints require larger, longer trials.',
    ],
    [
      'Why may non-linear pharmacokinetics (capacity-limited metabolism) lead to disproportionate exposure increases at higher doses?',
      [
        'Enzyme saturation makes clearance decrease as dose rises',
        'Non-linear kinetics only occur at very high drug concentrations',
        'Non-linear kinetics simplify dosing calculations',
        'All drugs follow linear kinetics across all dose ranges',
      ],
      0,
      'Capacity-limited metabolism shows exponential concentration increases; small dose increases cause large AUC jumps.',
    ],
  ],
  professor: [
    [
      'What is cooperativity in allosteric modulation, and how does a Hill coefficient >1 predict binding behavior?',
      [
        'Hill n>1 indicates positive cooperativity',
        'Hill coefficients are always independent of binding mechanism',
        'Cooperativity is irrelevant to GPCR signaling',
        'Hill n>1 always indicates agonism rather than allosterism',
      ],
      0,
      'Cooperativity arises from allosteric communication; n>1 produces sigmoidal curves steeper than single-site.',
    ],
    [
      'How do meta-analyses of CYP3A4 inhibition across substrates reveal hierarchical DDI risk stratification?',
      [
        'Ranked by fold-change in substrate AUC',
        'All CYP3A4 inhibitors produce identical DDIs',
        'Substrate concentration is irrelevant to DDI severity',
        'CYP3A4 inhibition always occurs at steady state',
      ],
      0,
      'FDA/EMA guidance ranks inhibitors by AUC fold-change; narrow-index substrates require dose adjustment.',
    ],
    [
      'What are the mechanistic principles underlying PBPK model extrapolation from in vitro clearance to in vivo pharmacokinetics?',
      [
        'In vitro CLint is scaled by hepatic blood flow',
        'PBPK models ignore in vitro data',
        'Scaling factors are not needed to translate in vitro to in vivo',
        'PBPK models always predict clinical PK perfectly',
      ],
      0,
      'PBPK uses well-stirred models to scale CLint; absolute bioavailability depends on enterocyte extraction.',
    ],
    [
      'How is biased signaling via β-arrestin pathway activation at muscarinic receptors therapeutically exploited for Alzheimer\'s?',
      [
        'β-arrestin coupling activates MAPK/ERK without G-protein',
        'β-arrestin always mediates the same effects as G-protein signaling',
        'β-arrestin coupling only occurs at inhibitory receptors',
        'Therapeutic selectivity requires simultaneous G-protein and β-arrestin activation',
      ],
      0,
      'M1 PAMs preferentially engage β-arrestin; uncouples pro-cognitive effects from cholinergic side effects.',
    ],
    [
      'How are population PK/PD models with Bayesian forecasting used to optimize pediatric dosing when efficacy data are sparse?',
      [
        'Allometric scaling for pediatric physiology',
        'Pediatric dosing is always a fraction of adult doses',
        'Bayesian forecasting is only used in elderly populations',
        'Population PK models ignore maturation of drug-metabolizing enzymes',
      ],
      0,
      'Population PK uses prior distributions; allometric exponents (0.75 CL, 1.0 V) adjust for size/maturation.',
    ],
    [
      'What is the mechanism of a triple drug-drug-gene interaction, and how might a poor metabolizer on competing CYP3A4 modulators be stratified?',
      [
        'Competing PK and pharmacogenomic effects',
        'Triple interactions cannot be predicted clinically',
        'Gene-drug interactions are always negligible',
        'Triple interactions only occur at very high doses',
      ],
      0,
      'CYP2D6 PMs on competing CYP3A4 modifiers experience altered exposure; phenotype prediction is complex.',
    ],
    [
      'How do substrate vs. inhibitor vs. inducer hierarchies predict DDI severity at CYP3A4, OATP1B1, and P-gp?',
      [
        'Rank depends on affinity and Cmax-to-Ki ratio',
        'All substrates interact identically with inhibitors',
        'Hierarchies are independent of drug concentration',
        'Inducers never cause clinically significant DDIs',
      ],
      0,
      'FDA guidance ranks substrates/inhibitors by fold-change thresholds; high Cmax/Ki poses highest DDI risk.',
    ],
    [
      'How do stereoselective transporters (e.g., L-DOPA efflux via OATP3) and metabolism interact to determine enantiomeric bioavailability?',
      [
        'Transporter selectivity can exceed metabolism\'s',
        'Transporters do not distinguish between enantiomers',
        'Metabolism always dominates transporter-mediated clearance',
        'Stereoselectivity only applies to metabolism',
      ],
      0,
      'OATP3-mediated L-DOPA efflux is stereoselective; inhibitors enable chiral reuse of inactive enantiomers.',
    ],
    [
      'What are the phases of target validation in oncology, and how do they differ from infectious disease target validation?',
      [
        'Oncology uses PDX models; ID uses resistance studies',
        'Oncology and ID target validation are identical processes',
        'Oncology targets require no preclinical validation',
        'ID targets are validated exclusively in animal models',
      ],
      0,
      'Oncology emphasizes genotype-phenotype linkage; ID focuses on mechanistic virulence loss and resistance barriers.',
    ],
    [
      'How are adaptive Phase II trial designs (e.g., seamless Phase II/III) statistically controlled for multiplicity, and what are regulatory implications?',
      [
        'Alpha-spending controls error at interim analyses',
        'Adaptive trials require no statistical adjustments',
        'Seamless designs always exceed the Type I error rate',
        'Adaptive designs are not acceptable to FDA/EMA',
      ],
      0,
      'FDA/EMA support pre-specified interim analyses (Pocock/OBrien-Fleming) with alpha-allocation to dose selection.',
    ],
    [
      'What is the role of epigenetic modifications (DNA methylation, histone acetylation) in modulating pharmacogene expression across populations?',
      [
        'Differential promoter methylation alters enzyme expression',
        'Epigenetic modifications are irrelevant to drug metabolism',
        'Methylation patterns are fixed and do not vary between tissues',
        'Only genetic polymorphisms matter for drug metabolism',
      ],
      0,
      'CYP450 promoter methylation silences expression in some individuals; correlates with altered metabolism.',
    ],
    [
      'How do mechanistically non-linear pharmacokinetics (saturable metabolism, renal reabsorption) affect label dosing and TDM?',
      [
        'Saturable metabolism causes dose-dependent half-lives',
        'All drugs follow linear kinetics, making TDM unnecessary',
        'Non-linearity only affects renal excretion',
        'Therapeutic drug monitoring is always inaccurate for non-linear drugs',
      ],
      0,
      'Phenytoin and salicylates show saturable metabolism; dose-dependent half-lives necessitate TDM.',
    ],
    [
      'What are transporter-mediated PK/PD biomarkers, and how can they refine patient stratification in renal/hepatic disease?',
      [
        'Transporter activity predicts individual clearance variance',
        'Transporters have no influence on disease-specific drug clearance',
        'Transporter function cannot be measured clinically',
        'Renal disease always reduces drug clearance proportionally',
      ],
      0,
      'Transporter activity predicts hepatic drug clearance; preserved function in CKD enables renally-excreted drugs.',
    ],
    [
      'How do regulatory pathways (505(b)(1) NDA, 505(b)(2), ANDA) influence generic drug development timelines?',
      [
        'ANDA needs only bioequivalence, no new trials',
        'All drug approvals follow identical regulatory pathways',
        '505(b)(1) and 505(b)(2) are equivalent in requirements',
        'Generic drugs do not require FDA approval',
      ],
      0,
      'Generic pathway (ANDA) requires bioequivalence without clinical trials; timelines compressed post-patent.',
    ],
    [
      'How do mechanistic insights into clinical trial failures (e.g., ENHANCE trial, lorcaserin removal) inform post-hoc PK/PD analyses?',
      [
        'Mechanism-based analyses guide salvage or discontinuation',
        'Clinical failures are always terminal endpoints',
        'Post-hoc PK/PD analyses cannot predict safety in new populations',
        'Mechanistic failures are irreversible',
      ],
      0,
      'ENHANCE failed despite LDL lowering—non-lipid mechanisms revealed. Lorcaserin showed cardiac valvulopathy.',
    ],
  ],
};
